import epicData from '../mock/mock-epics.json';
import featureData from '../mock/mock-features.json';
import backlogItemData from '../mock/mock-backlog-items.json';
import taskData from '../mock/mock-tasks.json';
import hierarchyData from '../mock/mock-hierarchy.json';

// Simulate API delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Get all epics or filtered epics
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Array>} - Array of epics
 */
export const getEpics = async (filters = {}) => {
  await delay(500); // Simulate network delay
  
  let filteredEpics = [...epicData];
  
  // Apply filters if any
  if (filters.state) {
    filteredEpics = filteredEpics.filter(epic => 
      epic.state.toLowerCase() === filters.state.toLowerCase()
    );
  }
  
  if (filters.fixVersion) {
    filteredEpics = filteredEpics.filter(epic => 
      epic.fixVersion === filters.fixVersion
    );
  }
  
  if (filters.priority) {
    filteredEpics = filteredEpics.filter(epic => 
      epic.priority === parseInt(filters.priority)
    );
  }
  
  return filteredEpics;
};

/**
 * Get all features or filtered features
 * @param {String} epicId - Optional epic ID to filter by
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Array>} - Array of features
 */
export const getFeatures = async (epicId = null, filters = {}) => {
  await delay(500);
  
  let filteredFeatures = [...featureData];
  
  if (epicId) {
    filteredFeatures = filteredFeatures.filter(feature => 
      feature.parentId === epicId
    );
  }
  
  // Apply filters if any
  if (filters.state) {
    filteredFeatures = filteredFeatures.filter(feature => 
      feature.state.toLowerCase() === filters.state.toLowerCase()
    );
  }
  
  if (filters.fixVersion) {
    filteredFeatures = filteredFeatures.filter(feature => 
      feature.fixVersion === filters.fixVersion
    );
  }
  
  return filteredFeatures;
};

/**
 * Get all backlog items or filtered backlog items
 * @param {String} featureId - Optional feature ID to filter by
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Array>} - Array of backlog items
 */
export const getBacklogItems = async (featureId = null, filters = {}) => {
  await delay(500);
  
  let filteredItems = [...backlogItemData];
  
  if (featureId) {
    filteredItems = filteredItems.filter(item => 
      item.parentId === featureId
    );
  }
  
  // Apply filters
  if (filters.workItemType) {
    filteredItems = filteredItems.filter(item => 
      item.workItemType === filters.workItemType
    );
  }
  
  if (filters.state) {
    filteredItems = filteredItems.filter(item => 
      item.state.toLowerCase() === filters.state.toLowerCase()
    );
  }
  
  return filteredItems;
};

/**
 * Get all tasks or filtered tasks
 * @param {String} backlogItemId - Optional backlog item ID to filter by
 * @returns {Promise<Array>} - Array of tasks
 */
export const getTasks = async (backlogItemId = null) => {
  await delay(500);
  
  if (backlogItemId) {
    return taskData.filter(task => task.parentId === backlogItemId);
  }
  
  return taskData;
};

/**
 * Get hierarchical view of work items
 * @param {String} epicId - Optional epic ID to filter by
 * @returns {Promise<Array>} - Array of epics with nested features, backlog items, and tasks
 */
export const getHierarchy = async (epicId = null) => {
  await delay(800);
  
  if (epicId) {
    return hierarchyData.filter(epic => epic.id === epicId);
  }
  
  return hierarchyData;
};

/**
 * Get completion percentage for an epic
 * @param {String} epicId - Epic ID
 * @returns {Promise<Number>} - Completion percentage
 */
export const getEpicCompletionPercentage = async (epicId) => {
  await delay(300);
  
  const epic = epicData.find(e => e.id === epicId);
  return epic ? epic.completionPercentage : 0;
};

/**
 * Get completion percentage for a feature
 * @param {String} featureId - Feature ID
 * @returns {Promise<Number>} - Completion percentage
 */
export const getFeatureCompletionPercentage = async (featureId) => {
  await delay(300);
  
  const feature = featureData.find(f => f.id === featureId);
  return feature ? feature.completionPercentage : 0;
};

/**
 * Get all unique fix versions
 * @returns {Promise<Array>} - Array of unique fix versions
 */
export const getFixVersions = async () => {
  await delay(300);
  
  const versions = new Set();
  
  epicData.forEach(epic => {
    if (epic.fixVersion) {
      versions.add(epic.fixVersion);
    }
  });
  
  featureData.forEach(feature => {
    if (feature.fixVersion) {
      versions.add(feature.fixVersion);
    }
  });
  
  return [...versions].sort();
};

/**
 * Get all unique states
 * @returns {Promise<Array>} - Array of unique states
 */
export const getStates = async () => {
  await delay(300);
  
  const states = new Set();
  
  epicData.forEach(epic => {
    if (epic.state) {
      states.add(epic.state);
    }
  });
  
  featureData.forEach(feature => {
    if (feature.state) {
      states.add(feature.state);
    }
  });
  
  backlogItemData.forEach(item => {
    if (item.state) {
      states.add(item.state);
    }
  });
  
  return [...states].sort();
};

/**
 * Get all unique priorities
 * @returns {Promise<Array>} - Array of unique priorities
 */
export const getPriorities = async () => {
  await delay(300);
  
  const priorities = new Set();
  
  epicData.forEach(epic => {
    if (epic.priority) {
      priorities.add(epic.priority);
    }
  });
  
  return [...priorities].sort();
};

/**
 * Get all unique work item types
 * @returns {Promise<Array>} - Array of unique work item types
 */
export const getWorkItemTypes = async () => {
  await delay(300);
  
  const types = new Set();
  
  backlogItemData.forEach(item => {
    if (item.workItemType) {
      types.add(item.workItemType);
    }
  });
  
  return [...types].sort();
};

/**
 * Get all unique teams (derived from area paths)
 * @returns {Promise<Array>} - Array of unique teams
 */
export const getTeams = async () => {
  await delay(300);
  
  const teams = new Set();
  
  epicData.forEach(epic => {
    if (epic.areaPath) {
      const parts = epic.areaPath.split('\\');
      if (parts.length > 1) {
        teams.add(parts[1]);
      }
    }
  });
  
  return [...teams].sort();
};

/**
 * Get metrics for dashboard
 * @returns {Promise<Object>} - Object containing various metrics
 */
export const getDashboardMetrics = async () => {
  await delay(800);
  
  // Count items by state
  const epicsByState = {};
  epicData.forEach(epic => {
    epicsByState[epic.state] = (epicsByState[epic.state] || 0) + 1;
  });
  
  const featuresByState = {};
  featureData.forEach(feature => {
    featuresByState[feature.state] = (featuresByState[feature.state] || 0) + 1;
  });
  
  // Count items by priority
  const epicsByPriority = {};
  epicData.forEach(epic => {
    epicsByPriority[epic.priority] = (epicsByPriority[epic.priority] || 0) + 1;
  });
  
  // Calculate average completion percentage
  const totalCompletionPercentage = epicData.reduce((sum, epic) => sum + epic.completionPercentage, 0);
  const averageCompletionPercentage = Math.round(totalCompletionPercentage / epicData.length);
  
  return {
    totalEpics: epicData.length,
    totalFeatures: featureData.length,
    totalBacklogItems: backlogItemData.length,
    totalTasks: taskData.length,
    epicsByState,
    featuresByState,
    epicsByPriority,
    averageCompletionPercentage
  };
};
